import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { BehaviorsAdminApi } from '../adminApi.js'
import type { ClientsAdminApi } from '../../clients/index.js'
import { BehaviorsManager, MASTER_SCOPE, type BehaviorsScope } from '../components/BehaviorsManager.js'
import { LoadFailure, Skeleton } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = BehaviorsAdminApi & ClientsAdminApi

/**
 * The behaviors manager for any client, for the platform admin: pick a
 * client, then edit its master, its custom behaviors and each facility's
 * overrides exactly as that client's own operator would.
 */
export function ClientBehaviorsPage({ api }: { api: AdminApi }) {
  const clientsQuery = useQuery({ queryKey: ['admin', 'clients'], queryFn: () => api.listClients() })
  const clients = (clientsQuery.data ?? []).filter((client) => client.clientId !== null).sort((a, b) => a.organizationName.localeCompare(b.organizationName))
  const [clientId, setClientId] = useState('')
  const [scope, setScope] = useState<BehaviorsScope>(MASTER_SCOPE)
  const behaviorsApi = useMemo(() => (clientId === '' ? null : api.behaviorsFor(clientId)), [api, clientId])

  return (
    <div className="max-w-5xl mx-auto px-7 py-6">
      {clientsQuery.isLoading ? (
        <Skeleton lines={5} label="Loading clients" />
      ) : clientsQuery.isError ? (
        <LoadFailure message="Failed to load clients. Confirm the API is reachable." onRetry={() => void clientsQuery.refetch()} retrying={clientsQuery.isFetching} />
      ) : (
        <>
          <div className="mb-5 max-w-sm">
            <label htmlFor="behaviors-client" className="block text-xs font-bold text-ink-mid mb-1">
              Client
            </label>
            <select
              id="behaviors-client"
              className="w-full rounded border border-marine/30 bg-white px-3 py-2 text-sm text-ink focus:outline-none focus:border-marine"
              value={clientId}
              onChange={(event) => {
                setClientId(event.target.value)
                // Facility ids belong to the client, so a new client starts on its master.
                setScope(MASTER_SCOPE)
              }}
            >
              <option value="">Choose a client…</option>
              {clients.map((client) => (
                <option key={client.registrationId} value={client.clientId ?? ''}>
                  {client.organizationName} ({client.environment})
                </option>
              ))}
            </select>
          </div>
          {behaviorsApi ? (
            // Keyed so one client's unsaved draft can never be shown, or saved, against another.
            <BehaviorsManager key={clientId} api={behaviorsApi} scope={scope} onScopeChange={setScope} />
          ) : (
            <div className="text-sm text-ink-mid">Choose a client to manage what its facilities measure.</div>
          )}
        </>
      )}
    </div>
  )
}
